/**
 * Environment Configuration Utilities
 * 
 * Provides safe runtime access to environment variables
 * to avoid issues with Next.js edge runtime and deployment environments
 */

/**
 * Get environment variable value at runtime
 * @param key The environment variable key
 * @param defaultValue Optional default value
 * @returns The environment variable value or default
 */
export function getEnvVar(key: string, defaultValue?: string): string | undefined {
  // Ensure we're accessing env vars at runtime, not module load time
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue;
  }
  return defaultValue;
}

/**
 * Get required environment variable value at runtime
 * @param key The environment variable key
 * @throws Error if the environment variable is not set
 * @returns The environment variable value
 */
export function getRequiredEnvVar(key: string): string {
  const value = getEnvVar(key);
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
}

/**
 * Assembly AI Configuration
 */
export function getAssemblyAIConfig() {
  return {
    apiKey: getEnvVar('ASSEMBLY_AI_API_KEY', ''),
    webhookUrl: getEnvVar('ASSEMBLY_AI_WEBHOOK_URL', ''),
    timeout: parseInt(getEnvVar('TRANSCRIPT_PROCESSING_TIMEOUT', '600000') || '600000'),
  };
}

/**
 * Supabase Configuration
 */
export function getSupabaseConfig() {
  return {
    url: getEnvVar('NEXT_PUBLIC_SUPABASE_URL') || getEnvVar('SUPABASE_URL') || '',
    anonKey: getEnvVar('NEXT_PUBLIC_SUPABASE_ANON_KEY') || getEnvVar('SUPABASE_ANON_KEY') || '',
    serviceKey: getEnvVar('SUPABASE_SERVICE_ROLE_KEY') || '',
  };
}

/**
 * S3 Configuration
 */
export function getS3Config() {
  return {
    bucket: getEnvVar('S3_BUCKET', 'ats-recordings'),
    region: getEnvVar('S3_REGION', 'us-west-1'),
    accessKeyId: getEnvVar('AWS_ACCESS_KEY_ID', ''),
    secretAccessKey: getEnvVar('AWS_SECRET_ACCESS_KEY', ''),
  };
}

/**
 * Check if running in production
 */
export function isProduction(): boolean {
  return getEnvVar('NODE_ENV') === 'production';
}

/**
 * Check if running in development
 */
export function isDevelopment(): boolean {
  return getEnvVar('NODE_ENV') === 'development';
}

/**
 * Get the deployment environment (staging, production, etc)
 */
export function getDeploymentEnv(): string {
  return getEnvVar('VERCEL_ENV') || getEnvVar('NODE_ENV') || 'development';
}